import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { useMemo } from 'react';
import colors from 'assets/scss/colors.module.scss';
import { TimeSeriesChart, ITimeSeriesChartConfig } from 'app/components/common/charts/TimeSeriesChart';

interface ILeadNowHistoryChart {
	agtId?: number;
	agtIds?: number[];
}

interface LeadNowChartData {
	kw: string;
	kwRaw: number;
	brutto: number;
	storno: number;
	netto: number;
}

export const LeadNowHistoryChart = observer((props: ILeadNowHistoryChart) => {
	const { agtLeadNowUiStore } = useStore();
	const { agtId, agtIds } = props;

	// Build chart configuration
	const chartConfig: ITimeSeriesChartConfig<LeadNowChartData> = useMemo(
		() => ({
			title: 'AMIS.NOW Leads Entwicklung (letzte 12 Wochen)',
			tag: 'AMIS.NOW Leads',
			tagColor: 'turquoise',
			height: 400,

			xAxisField: 'kw',
			xAxisRotation: 45,

			// Define Y-axes
			yAxes: [
				{
					name: 'Anzahl Leads',
					position: 'left',
				},
			],

			// Define derived fields (calculated at render time)
			derivedFields: [
				{
					fieldName: 'stornierungen',
					calculate: (d) => d.brutto - d.netto,
				},
			],

			// Define series
			series: [
				{
					name: 'Netto Leads',
					dataField: 'netto',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.turquoise,
					stack: 'leads',
					barMaxWidth: 40,
				},
				{
					name: 'Stornierte Leads',
					dataField: 'stornierungen',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.greyLight,
					stack: 'leads',
					barMaxWidth: 40,
				},
			],

			// Custom tooltip to show brutto, netto, and stornierungen
			tooltipFormatter: (params: any) => {
				if (!Array.isArray(params) || params.length === 0) return '';

				const xValue = params[0]?.axisValue || '';

				// Extract values from the stacked bars
				// params[0] is Netto Leads, params[1] is Stornierte Leads
				const netto = params[0]?.value || 0;
				const stornierungen = params[1]?.value || 0;
				const brutto = netto + stornierungen;

				let result = `<strong>${xValue}</strong><br/>`;
				result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${colors.turquoise};"></span> Brutto Leads: <strong>${brutto}</strong><br/>`;
				result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${colors.turquoise};"></span> Netto Leads: <strong>${netto}</strong><br/>`;
				result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${colors.greyLight};"></span> Stornierte Leads: <strong>${stornierungen}</strong><br/>`;

				return result;
			},

			// Data loader function
			dataLoader: async (ids) => {
				if (Array.isArray(ids)) {
					return agtLeadNowUiStore.loadLeadNowHistoryLast12Weeks(ids);
				} else {
					return agtLeadNowUiStore.loadLeadNowHistoryLast12Weeks(ids);
				}
			},
		}),
		[agtLeadNowUiStore],
	);

	return <TimeSeriesChart config={chartConfig} agtId={agtId} agtIds={agtIds} />;
});
